import { useCallback, useState } from 'react';
import { generateInvoiceService as defaultService } from '../services/generateInvoiceService';

// ─── Hook ─────────────────────────────────────────────────────────────────────

const INITIAL_STATE = { status: 'idle', error: null, result: null };

/**
 * One-shot mutation hook for the check_duplicate → generate_invoice flow.
 *
 * Sequence:
 *   1. Validate companies array is non-empty
 *   2. check_duplicate_invoice per company (API takes single company_id)
 *      — abort with DUPLICATE_INVOICE if any exist, naming the company
 *   3. generate_company_invoice once with all company IDs (API takes [String]!)
 *
 * `service` is injected for testability — pass a mock in tests.
 */
export function useGenerateInvoice(userId, service = defaultService) {
  const [state, setState] = useState(INITIAL_STATE);

  const checkAndGenerate = useCallback(
    async ({
      companies,
      fromDate,
      toDate,
      paymentDate,
      autoGeneratePdf,
      autoSendEmail,
      requireApproval,
      // isKraveinFeeInclude,
      isCashInclude,
      allCompanies,
    }) => {
      if (!allCompanies && !companies?.length) {
        setState({
          status: 'error',
          error: {
            message:
              'At least one company must be selected before generating invoices.',
            code: 'MISSING_COMPANY_ID',
          },
          result: null,
        });
        return null;
      }

      setState({ status: 'loading', error: null, result: null });

      try {
        if (!allCompanies) {
          const duplicateChecks = await Promise.all(
            companies.map(company =>
              service
                .checkDuplicateInvoice({
                  userId,
                  companyId: company.companyId,
                  fromDate,
                  toDate,
                })
                .then(result => ({ company, exists: result.exists })),
            ),
          );
          const firstDuplicate = duplicateChecks.find(d => d.exists);
          if (firstDuplicate) {
            const label = firstDuplicate.company.name
              ? `"${firstDuplicate.company.name}"`
              : 'a selected company';
            setState({
              status: 'error',
              error: {
                message: `An invoice already exists for ${label} and the selected period.`,
                code: 'DUPLICATE_INVOICE',
              },
              result: null,
            });
            return null;
          }
        }

        const generated = await service.generateCompanyInvoice({
          userId,
          companyIds: allCompanies ? [] : companies.map(c => c.companyId),
          fromDate,
          toDate,
          paymentDate,
          autoSendEmail: false,
          isCashInclude,
          // isKraveinFeeInclude,
          allCompanies,
        });

        setState({ status: 'success', error: null, result: generated });
        return generated;
      } catch (err) {
        setState({ status: 'error', error: err, result: null });
        return null;
      }
    },
    [userId, service],
  );

  const resetGenerate = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    generateStatus: state.status,
    generateError: state.error,
    generateResult: state.result,
    isGenerating: state.status === 'loading',
    checkAndGenerate,
    resetGenerate,
  };
}
